import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import LoaderIcon from '../../../../../src/components/LoaderImg/loader.gif';
import {
  getAppointmentSlotsAction,
  getAppointmentSlotsResetAction,
  cancelRecurringSeriesAction,
  cancelRecurringSeriesResetAction,
} from 'src/redux/actions/appointmentAction';
import { getPayerCateGroup } from 'src/layouts/components/utils-method';
import useCurrentUser from 'src/hooks/use-current-user';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { recurringAppointmentBookFlag } from 'src/config';

interface Props {
  open: boolean;
  onClose: () => void;
  therapist: any;
  localDate: string;
  onEditAppointment: (appointment: any) => void;
  onDeleteAppointment: (appointment: any) => void;
  therapistsList: any[];
}

const ViewAppointmentsModal: React.FC<Props> = ({
  open,
  onClose,
  therapist,
  localDate,
  onEditAppointment,
  onDeleteAppointment,
  therapistsList,
}) => {
  const dispatch = useDispatch();
  const [providerId] = useCurrentUser(['providerId']);

  const [selectedTherapistUuid, setSelectedTherapistUuid] = useState<string>('');
  const [showRecurringEditPopup, setShowRecurringEditPopup] = useState<boolean>(false);
  const [selectedAppointmentForEdit, setSelectedAppointmentForEdit] = useState<any>(null);
  const [showRecurringCancelPopup, setShowRecurringCancelPopup] = useState<boolean>(false);
  const [selectedAppointmentForCancel, setSelectedAppointmentForCancel] = useState<any>(null);

  const appointmentSlotsState = useSelector((state: any) => state?.getAppointmentSlotsReducer);
  const cancelRecurringSeriesState = useSelector(
    (state: any) => state?.cancelRecurringSeriesReducer
  );

  const loading = appointmentSlotsState?.loading || false;
  const appointmentData = appointmentSlotsState?.response || [];

  const [isRecurringAppointmentBook] = useFeatureToggleWhenReady(
    recurringAppointmentBookFlag,
    'FACILITY'
  );

  useEffect(() => {
    if (therapist && open) {
      setSelectedTherapistUuid(therapist.uuid);
    }
  }, [therapist, open]);

  useEffect(() => {
    if (selectedTherapistUuid && open && localDate) {
      // Find the therapist's discipline
      const currentTherapist = therapistsList.find((t) => t.uuid === selectedTherapistUuid);
      const discipline = currentTherapist?.discipline || therapist?.discipline || 'PT';

      dispatch(
        getAppointmentSlotsAction({
          therapistUuid: selectedTherapistUuid,
          discipline,
          localDate,
        })
      );
    }
  }, [selectedTherapistUuid, open, localDate, dispatch, therapistsList, therapist]);

  // Handle cancel recurring series success/error
  useEffect(() => {
    if (cancelRecurringSeriesState?.success) {
      setShowRecurringCancelPopup(false);
      setSelectedAppointmentForCancel(null);
      dispatch(cancelRecurringSeriesResetAction());

      // Refresh appointments list
      if (selectedTherapistUuid && localDate) {
        const currentTherapist = therapistsList.find((t) => t.uuid === selectedTherapistUuid);
        const discipline = currentTherapist?.discipline || therapist?.discipline || 'PT';
        dispatch(
          getAppointmentSlotsAction({
            therapistUuid: selectedTherapistUuid,
            discipline,
            localDate,
          })
        );
      }
    } else if (cancelRecurringSeriesState?.error && !cancelRecurringSeriesState?.loading) {
      console.error('Cancel recurring series error:', cancelRecurringSeriesState?.error);
      dispatch(cancelRecurringSeriesResetAction());
    }
  }, [
    cancelRecurringSeriesState?.success,
    cancelRecurringSeriesState?.error,
    cancelRecurringSeriesState?.loading,
    selectedTherapistUuid,
    localDate,
    therapistsList,
    therapist,
    dispatch,
  ]);

  // Generate time slots from 7 AM to 5 PM
  const generateTimeSlots = useMemo(() => {
    const slots = [];
    const startHour = 7;
    const endHour = 17;

    for (let hour = startHour; hour <= endHour; hour++) {
      const time24 = `${hour.toString().padStart(2, '0')}:00:00`;
      const timeMoment = moment(time24, 'HH:mm:ss');
      const time12 = timeMoment.format('h:mm A');

      slots.push({
        time24,
        time12,
        hour: timeMoment.format('hA'),
      });
    }

    return slots;
  }, []);

  // Get assigned patients (those without appointmentStartTime)
  const assignedInpatients: string[] = useMemo(() => {
    if (!appointmentData || !Array.isArray(appointmentData)) {
      return [];
    }

    return appointmentData
      .filter((patient: any) => !patient.appointmentStartTime)
      .map((patient: any) => {
        const duration = patient.numberOfMinutes || '';
        const durationStr = duration ? ` (${duration} Min.)` : '';
        return `${patient.patientLastname}, ${patient.patientFirstname}${durationStr}`;
      });
  }, [appointmentData]);

  // Create a map of scheduled appointments by hour
  const scheduledAppointmentsMap = useMemo(() => {
    const map: { [key: string]: any[] } = {};

    if (appointmentData && Array.isArray(appointmentData)) {
      appointmentData
        ?.filter((patient: any) => patient.appointmentStartTime)
        ?.forEach((patient: any) => {
          const hour = moment(patient?.appointmentStartTime, moment.ISO_8601).format('hA');
          if (!map[hour]) {
            map[hour] = [];
          }

          // Calculate duration from appointmentEndTime - appointmentStartTime
          let duration = '';
          if (patient?.appointmentStartTime && patient?.appointmentEndTime) {
            const startTime = moment(patient.appointmentStartTime, moment.ISO_8601);
            const endTime = moment(patient.appointmentEndTime, moment.ISO_8601);
            const durationMinutes = endTime.diff(startTime, 'minutes');
            if (durationMinutes > 0) {
              duration = `${durationMinutes} Min.`;
            }
          } else if (patient?.numberOfMinutes) {
            // Fallback to numberOfMinutes if appointmentEndTime is not available
            duration = `${patient.numberOfMinutes} Min.`;
          }

          // Look up splitRef from therapist.assignedPatients
          const assignedPatient = therapist?.assignedPatients?.[patient?.patientUuid];
          const matchedAppt = assignedPatient?.appointments?.find(
            (appt: any) => appt?.taskUuid === patient?.taskUuid
          );
          const splitRef =
            patient?.splitRef ?? matchedAppt?.splitRef ?? assignedPatient?.splitRef ?? null;

          map[hour].push({
            ...patient,
            splitRef,
            time: moment(patient?.appointmentStartTime, moment.ISO_8601).format('h:mm A'),
            duration: duration,
          });
        });
    }

    return map;
  }, [appointmentData, therapist]);

  // Group by hour with all slots from 7 AM to 5 PM
  const groupedAppointments = useMemo(() => {
    return generateTimeSlots?.map((slot) => {
      const appointments = scheduledAppointmentsMap[slot.hour] || [];
      // Sort appointments by appointmentStartTime within each hour group
      const sortedAppointments = [...appointments].sort((a, b) => {
        const timeA = moment(a.appointmentStartTime, moment.ISO_8601).valueOf();
        const timeB = moment(b.appointmentStartTime, moment.ISO_8601).valueOf();
        return timeA - timeB;
      });
      return {
        hour: slot.hour,
        appointments: sortedAppointments,
      };
    });
  }, [generateTimeSlots, scheduledAppointmentsMap]);

  const handleTherapistChange = (event: SelectChangeEvent<string>) => {
    setSelectedTherapistUuid(event.target.value);
  };

  const handleCancel = () => {
    setSelectedTherapistUuid('');
    dispatch(getAppointmentSlotsResetAction());
    onClose();
  };

  const handleEditClick = (appointment: any) => {    
    if (appointment?.recurringAppointmentUuid && isRecurringAppointmentBook) {
      setSelectedAppointmentForEdit(appointment);
      setShowRecurringEditPopup(true);
    } else {
      onEditAppointment(appointment);
    }
  };

  const handleRecurringEditOption = (editAllFuture: boolean) => {
    if (selectedAppointmentForEdit) {
      const appointmentWithFlag = {
        ...selectedAppointmentForEdit,
        isRecurringEdit: editAllFuture,
      };
      onEditAppointment(appointmentWithFlag);
    }
    setShowRecurringEditPopup(false);
    setSelectedAppointmentForEdit(null);
  };

  const handleCloseRecurringEditPopup = () => {
    setShowRecurringEditPopup(false);
    setSelectedAppointmentForEdit(null);
  };

  const handleDeleteClick = (appointment: any) => {
    if (appointment?.recurringAppointmentUuid) {
      setSelectedAppointmentForCancel(appointment);
      setShowRecurringCancelPopup(true);
    } else {
      onDeleteAppointment(appointment);
    }
  };

  const handleRecurringCancelOption = (cancelAllFuture: boolean) => {
    if (selectedAppointmentForCancel) {
      if (cancelAllFuture) {
        // Call the cancel recurring series API
        const currentTherapist = therapistsList.find(
          (t) => t.uuid === selectedTherapistUuid || t.uuid === therapist?.uuid
        );
        const discipline = currentTherapist?.discipline || therapist?.discipline || 'PT';

        const payload = {
          recurringAppointmentUuid: selectedAppointmentForCancel.recurringAppointmentUuid,
          startDate: localDate, // YYYY-MM-DD format
          providerId: providerId || '',
          currentDate: localDate,
          discipline: discipline,
          type: '',
          active: true,
          skill: '',
          meta: {
            providerId: providerId || '',
            type: '',
            active: true,
            discipline: discipline,
            localDate: localDate,
            skill: '',
            taskPage: 0,
            therapistPage: 0,
          },
          isV2: true,
        };
        dispatch(cancelRecurringSeriesAction(payload));
      } else {
        onDeleteAppointment(selectedAppointmentForCancel);
        setShowRecurringCancelPopup(false);
        setSelectedAppointmentForCancel(null);
      }
    }
  };

  const handleCloseRecurringCancelPopup = () => {
    setShowRecurringCancelPopup(false);
    setSelectedAppointmentForCancel(null);
  };

  const getRecurrenceDetails = (appointment: any) => {
    if (!appointment?.recurringAppointmentUuid) return null;

    const appointmentDate = moment(appointment.appointmentStartTime);
    const dayOfWeek = appointmentDate.format('dddd');
    const endDate = appointment.recurrenceEndDate
      ? moment(appointment.recurrenceEndDate).format('MMM DD, YYYY')
      : 'N/A';

    return { dayOfWeek, endDate };
  };

  const isPastDate = useMemo(() => {
    if (!localDate) return false;
    const appointmentDate = moment(localDate, 'YYYY-MM-DD');
    const today = moment().startOf('day');
    return appointmentDate.isBefore(today);
  }, [localDate]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '16px',
            gap: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            pb: 1,
            p: 0,
          }}
        >
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 200,
                    border: '2px solid #969696 !important',
                    borderRadius: '8px !important',
                  }}
                >
                  <Select
                    value={selectedTherapistUuid}
                    onChange={handleTherapistChange}
                    displayEmpty
                    sx={{
                      fontSize: '16px !important',
                      fontWeight: 500,
                      '.MuiSelect-select': {
                        fontSize: '16px !important',
                      },
                    }}
                  >
                    {therapistsList.map((therapist) => (
                      <MenuItem
                        key={therapist.uuid}
                        value={therapist.uuid}
                      >
                        {therapist.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography
                  variant="h6"
                  fontWeight={600}
                >
                  Appointments | {moment(localDate, 'YYYY-MM-DD').format('MMM. DD')}
                </Typography>
              </Box>
              <IconButton
                onClick={handleCancel}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            pr: 1,
            '::-webkit-scrollbar': {
              width: '12px !important',
            },
          }}
        >
          <Box sx={{ mb: 2, pt: 1, pb: 0, borderTop: '1px solid #cacaca' }}>
            <Typography
              variant="body2"
              sx={{ fontSize: '13px', color: '#111927' }}
            >
              <strong>Assigned Inpatients:</strong>{' '}
              {assignedInpatients?.length > 0 ? assignedInpatients.join(', ') : 'None'}
            </Typography>
          </Box>

          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '300px',
              }}
            >
              <img
                src={LoaderIcon}
                alt="Loading..."
              />
            </Box>
          ) : (
            <TableContainer>
              <Table
                size="small"
                sx={{
                  borderCollapse: 'collapse',
                  border: '1px solid #cacaca',
                  '& .MuiTableCell-root': {
                    borderBottom: 'none',
                  },
                }}
              >
                <TableHead>
                  <TableRow sx={{ borderBottom: '1px solid #cacaca' }}>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: '100px',
                        fontSize: '13px',
                        py: 1.5,
                        background: '#e8f1fa !important',
                        textTransform: 'capitalize !important',
                        borderRight: '1px solid #cacaca',
                      }}
                    >
                      Time
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: '100px',
                        fontSize: '13px',
                        py: 1.5,
                        background: '#e8f1fa !important',
                        textTransform: 'capitalize !important',
                        borderRight: '1px solid #cacaca',
                      }}
                    >
                      Patient
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedAppointments?.map((group, groupIndex) => {
                    const backgroundColor = groupIndex % 2 === 0 ? '#FFFFFF' : '#cecaca1f';
                    const hasAppointments = group?.appointments?.length > 0;

                    if (hasAppointments) {
                      // Show all appointments for this hour
                      return group?.appointments?.map((appointment: any, index: number) => (
                        <TableRow
                          key={appointment?.patientUuid}
                          sx={{
                            backgroundColor: backgroundColor,
                            '&:hover': {
                              backgroundColor:
                                backgroundColor === '#FFFFFF' ? '#cecaca1f' : '#F3F4F6',
                            },
                          }}
                        >
                          {index === 0 && (
                            <TableCell
                              rowSpan={group?.appointments?.length}
                              sx={{
                                width: '100px',
                                maxWidth: '100px',
                                backgroundColor: backgroundColor,
                                borderRight: '2px solid #969696',
                                borderBottom: '2px solid #969696 !important',
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  verticalAlign: 'top',
                                  fontSize: '12px',
                                }}
                              >
                                {group?.hour}
                              </Typography>
                            </TableCell>
                          )}

                          <TableCell
                            sx={{
                              verticalAlign: 'top',
                              py: 1.5,
                              backgroundColor: backgroundColor,
                              borderBottom:
                                index === group?.appointments?.length - 1
                                  ? '2px solid #969696 !important'
                                  : 'none',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                  sx={{
                                    fontSize: '13px',
                                    color: '#6e6f6f',
                                    minWidth: '50px',
                                    fontWeight: 600,
                                  }}
                                >
                                  {appointment.time}
                                </Typography>
                                <Chip
                                  variant="outlined"
                                  size="small"
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {appointment?.recurringAppointmentUuid &&
                                        isRecurringAppointmentBook && (
                                          <RepeatRoundedIcon
                                            sx={{
                                              fontSize: '16px',
                                              color: '#6366F1',
                                            }}
                                          />
                                        )}
                                      <Typography
                                        component="span"
                                        sx={{
                                          fontSize: '13px',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {`${appointment?.patientLastname}, ${appointment?.patientFirstname}`}
                                      </Typography>
                                    </Box>
                                  }
                                  sx={{
                                    color: '#111927',
                                    fontFamily: 'Inter',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    width: 'fit-content',
                                    maxWidth: '200px',
                                    height: '22px',
                                    textAlign: 'left',
                                    textOverflow: 'ellipsis',
                                    borderRadius: '20px',
                                    background: '#FFF',
                                    borderTop: '1px solid #b9aeae !important',
                                    borderLeft: '1px solid #b9aeae !important',
                                    borderRight: '1px solid #b9aeae !important',
                                    borderBottom: (() => {
                                      const payerGroup = appointment?.payerPlanType
                                        ? getPayerCateGroup(appointment?.payerPlanType)
                                        : null;
                                      const borderColor = payerGroup?.borderColor || '#E5E7EB';
                                      return `2px solid ${borderColor} !important`;
                                    })(),
                                    '& .MuiChip-label': {
                                      padding: '0 12px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    },
                                  }}
                                />
                                <Typography
                                  sx={{ fontSize: '13px', color: '#6e6f6f', fontWeight: 600 }}
                                >
                                  {appointment?.numberOfMinutes} Min.
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip
                                  title={
                                    isPastDate
                                      ? 'Cannot edit past appointments'
                                      : 'Edit appointment'
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditClick(appointment)}
                                    disabled={isPastDate}
                                    sx={{
                                      color: isPastDate ? '#9CA3AF' : '#6e6f6f',
                                      borderRadius: '4px',
                                      cursor: isPastDate ? 'not-allowed' : 'pointer',
                                      ':hover': {
                                        backgroundColor: isPastDate
                                          ? 'transparent'
                                          : 'rgba(108, 117, 127, 0.1)',
                                      },
                                    }}
                                  >
                                    <ModeEditOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    isPastDate
                                      ? 'Cannot delete past appointments'
                                      : 'Delete appointment'
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteClick(appointment)}
                                    disabled={isPastDate}
                                    sx={{
                                      color: isPastDate ? '#9CA3AF' : '#EF4444',
                                      borderRadius: '4px',
                                      cursor: isPastDate ? 'not-allowed' : 'pointer',
                                      ':hover': {
                                        backgroundColor: isPastDate
                                          ? 'transparent'
                                          : 'rgba(239, 68, 68, 0.1)',
                                      },
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ));
                    } else {
                      return (
                        <TableRow
                          key={group.hour}
                          sx={{
                            backgroundColor: backgroundColor,
                          }}
                        >
                          <TableCell
                            sx={{
                              borderRight: '2px solid #969696',
                              borderBottom: '2px solid #969696 !important',
                              width: '100px',
                              maxWidth: '100px',
                              backgroundColor: backgroundColor,
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                verticalAlign: 'top',
                                fontSize: '12px',
                              }}
                            >
                              {group.hour}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              verticalAlign: 'top',
                              py: 1.5,
                              backgroundColor: backgroundColor,
                              borderBottom: '2px solid #969696 !important',
                            }}
                          >
                            <Typography sx={{ fontSize: '13px', color: '#797c81' }}>
                              No appointments
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    }
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'flex-end' }}>
          <Button
            onClick={handleCancel}
            variant="text"
            sx={{
              textTransform: 'none',
              color: '#6e6f6f',
              fontSize: '14px',
              border: '0.5px solid',
              '&:hover': {
                backgroundColor: '#cecaca1f',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showRecurringEditPopup}
        onClose={handleCloseRecurringEditPopup}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            padding: '24px',
            borderBottom: '1px solid #9d9595 !important',
          }}
        >
          <Box
            sx={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#F3F4F6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <EditOutlinedIcon sx={{ color: '#6366F1', fontSize: '20px' }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '18px',
                color: '#111927',
              }}
            >
              Edit Recurring Appointment
            </Typography>
            {selectedAppointmentForEdit && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '14px',
                  color: '#6e6f6f',
                }}
              >
                {`${selectedAppointmentForEdit?.patientLastname}, ${selectedAppointmentForEdit?.patientFirstname} at ${selectedAppointmentForEdit.time}`}
              </Typography>
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {selectedAppointmentForEdit && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderBottom: '1px solid #9d9595 !important',
                p: '18px 24px',
              }}
            >
              <RepeatRoundedIcon sx={{ fontSize: '16px', color: '#6366F1' }} />
              <Typography
                variant="body2"
                sx={{
                  fontSize: '14px',
                  color: '#6e6f6f',
                }}
              >
                {(() => {
                  const details = getRecurrenceDetails(selectedAppointmentForEdit);
                  if (details) {
                    const formattedDate = selectedAppointmentForEdit?.recurringSeriesEndDate
                      ? moment(selectedAppointmentForEdit.recurringSeriesEndDate).format(
                          'MMM D, YYYY'
                        )
                      : '';
                    return `Recurring every ${details?.dayOfWeek} until ${formattedDate}`;
                  }
                  return 'Recurring appointment';
                })()}
              </Typography>
            </Box>
          )}

          <Typography
            variant="body1"
            sx={{
              fontSize: '14px',
              color: '#111927',
              fontWeight: 500,
              p: '18px 24px 0px 24px',
            }}
          >
            This is part of a recurring series. What would you like to edit?
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: '18px 24px 0px 24px' }}>
            <Box
              onClick={() => handleRecurringEditOption(false)}
              sx={{
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                padding: '10px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                  borderColor: '#6366F1',
                },
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#111927',
                  mb: 0.5,
                }}
              >
                This appointment only
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '13px',
                  color: '#6e6f6f',
                }}
              >
                Only change the session on this date
              </Typography>
            </Box>

            <Box
              onClick={() => handleRecurringEditOption(true)}
              sx={{
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                padding: '10px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                  borderColor: '#6366F1',
                },
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#111927',
                  mb: 0.5,
                }}
              >
                This and all future appointments
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '13px',
                  color: '#6e6f6f',
                }}
              >
                Change this and all upcoming sessions from today onwards
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: '18px 24px', justifyContent: 'flex-end' }}>
          <Button
            onClick={handleCloseRecurringEditPopup}
            variant="text"
            sx={{
              textTransform: 'none',
              color: '#6e6f6f',
              fontSize: '14px',
              border: '1px solid #E5E7EB',
            }}
          >
            Never mind
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Recurring Appointment Popup */}
      <Dialog
        open={showRecurringCancelPopup}
        onClose={handleCloseRecurringCancelPopup}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            padding: '24px',
            borderBottom: '1px solid #9d9595 !important',
          }}
        >
          <Box
            sx={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#FEF2F2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DeleteOutlineIcon sx={{ color: '#EF4444', fontSize: '20px' }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '18px',
                color: '#111927',
              }}
            >
              Cancel Recurring Appointment
            </Typography>
            {selectedAppointmentForCancel && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '14px',
                  color: '#6e6f6f',
                }}
              >
                {`${selectedAppointmentForCancel?.patientLastname}, ${selectedAppointmentForCancel?.patientFirstname} at ${selectedAppointmentForCancel.time}`}
              </Typography>
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {selectedAppointmentForCancel && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderBottom: '1px solid #9d9595 !important',
                p: '18px 24px',
              }}
            >
              <RepeatRoundedIcon sx={{ fontSize: '16px', color: '#6366F1' }} />
              <Typography
                variant="body2"
                sx={{
                  fontSize: '14px',
                  color: '#6e6f6f',
                }}
              >
                {(() => {
                  const details = getRecurrenceDetails(selectedAppointmentForCancel);
                  if (details) {
                    return `Recurring every ${details?.dayOfWeek} until ${selectedAppointmentForCancel?.date}`;
                  }
                  return 'Recurring appointment';
                })()}
              </Typography>
            </Box>
          )}

          <Typography
            variant="body1"
            sx={{
              fontSize: '14px',
              color: '#111927',
              fontWeight: 500,
              p: '18px 24px 0px 24px',
            }}
          >
            This is part of a recurring series. What would you like to cancel?
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: '18px 24px 0px 24px' }}>
            <Box
              onClick={() => handleRecurringCancelOption(false)}
              sx={{
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                padding: '10px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                  borderColor: '#6366F1',
                },
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#111927',
                  mb: 0.5,
                }}
              >
                This appointment only
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '13px',
                  color: '#6e6f6f',
                }}
              >
                Only cancel the session on this date
              </Typography>
            </Box>

            <Box
              onClick={() => handleRecurringCancelOption(true)}
              sx={{
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                padding: '10px',
                cursor: 'pointer',
                backgroundColor: '#FEF2F2',
                borderColor: '#EF4444',
                '&:hover': {
                  backgroundColor: '#FEE2E2',
                  borderColor: '#DC2626',
                },
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#EF4444',
                  mb: 0.5,
                }}
              >
                This and all future appointments
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '13px',
                  color: '#6e6f6f',
                }}
              >
                Cancel this and all upcoming sessions from today onwards
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: '18px 24px', justifyContent: 'flex-end' }}>
          <Button
            onClick={handleCloseRecurringCancelPopup}
            variant="text"
            sx={{
              textTransform: 'none',
              color: '#6e6f6f',
              fontSize: '14px',
              border: '1px solid #E5E7EB',
            }}
          >
            Never mind
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ViewAppointmentsModal;